import { round } from '@/utils/mathUtils';
import dayjs from 'dayjs';
import {
  AlertCircle,
  Building2,
  Calendar,
  Clock9,
  DollarSign,
  Hash,
  Headset,
  Package,
  Printer,
  Receipt,
  TrendingUp,
  User,
} from 'lucide-react';

import { IncomePrintDialog } from '@/components/custom-ui/IncomePrintDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import type { IInvoiceDetails } from '../../../api/IncomingInvoice.interface';

import ItemsTable from './Table';

type Props = {
  invoice: IInvoiceDetails;
};

function InvoiceBlock({ invoice }: Props) {
  const formatCurrency = (value: number) =>
    Math.abs(round(value, 2)).toLocaleString();
  const getBalanceType = (balance: number) => {
    if (balance < 0)
      return { text: 'مدين', color: 'text-red-600 dark:text-red-400' };
    if (balance > 0)
      return { text: 'دائن', color: 'text-emerald-600 dark:text-emerald-400' };
    return { text: 'خالص', color: 'text-slate-600 dark:text-slate-400' };
  };

  // Debug: Check if invoice data is available

  const beforeBalance = getBalanceType(invoice?.header?.S_Balance || 0);
  const afterBalance = getBalanceType(invoice?.header?.S_BalanceAfter || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
      {/* Invoice Number Header */}

      {/* Main Content - Two Column Layout */}
      <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-[450px_1fr]">
        {/* Left Column - Invoice Information */}
        <div className="space-y-6">
          <div className="mb-8 w-full">
            <div className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-700 p-6 shadow-xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-20">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-full w-full"
                >
                  <g fill="none" fillRule="evenodd">
                    <g fill="rgba(255,255,255,0.1)">
                      <circle cx="30" cy="30" r="2" />
                    </g>
                  </g>
                </svg>
              </div>

              {/* Floating Elements */}
              <div className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                <Receipt className="h-4 w-4 text-white" />
              </div>
              <div className="absolute -bottom-3 -left-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                <Hash className="h-4 w-4 text-white" />
              </div>

              {/* Main Content */}
              <div className="relative flex flex-col items-center text-center sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 shadow-lg backdrop-blur-sm">
                    <Receipt className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white sm:text-lg lg:text-xl">
                      رقم الفاتورة
                    </h1>
                    <p className="text-2xl text-indigo-100 sm:text-4xl">
                      {invoice?.header?.InvoiceID || 'غير متوفر'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3 sm:mt-0">
                  <IncomePrintDialog invoice={invoice}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="group relative overflow-hidden border-white/30 bg-white/10 text-white shadow-sm backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:shadow-md"
                    >
                      <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-white/0 via-white/20 to-white/0 transition-transform duration-700 group-hover:translate-x-full"></div>
                      <Printer className="mr-2 h-4 w-4" />
                      <span className="hidden sm:inline">طباعة الفاتورة</span>
                      <span className="sm:hidden">طباعة</span>
                    </Button>
                  </IncomePrintDialog>
                </div>
              </div>
            </div>
          </div>
          {/* Supplier Information Card */}
          <Card className="border-0 bg-gradient-to-br from-white/90 to-slate-50/90 shadow-xl backdrop-blur-sm dark:from-slate-900/90 dark:to-slate-800/90">
            <CardHeader className="border-b border-slate-200/50 bg-slate-50/30 px-6 py-4 dark:border-slate-700/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl text-slate-900 dark:text-slate-100">
                    {invoice?.header?.SupplierName}
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    معلومات المورد والفاتورة
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Invoice Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:from-blue-950/30 dark:to-indigo-950/30">
                    <div className="mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        التاريخ
                      </span>
                    </div>
                    <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                      {dayjs(new Date(invoice?.header?.TrDate)).format(
                        'DD/MM/YYYY',
                      )}
                    </p>
                  </div>

                  <div className="rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 p-4 dark:from-amber-950/30 dark:to-orange-950/30">
                    <div className="mb-2 flex items-center gap-2">
                      <Clock9 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        الوقت
                      </span>
                    </div>
                    <p className="text-lg font-bold text-amber-900 dark:text-amber-100">
                      {dayjs(new Date(invoice?.header?.TrTime)).format('HH:mm')}
                    </p>
                  </div>
                </div>

                {/* Employee Info */}
                <div className="rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 p-4 dark:from-purple-950/30 dark:to-pink-950/30">
                  <div className="mb-2 flex items-center gap-2">
                    <User className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      الموظف المسؤول
                    </span>
                  </div>
                  <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                    {invoice?.header?.EmployeeName}
                  </p>
                </div>

                {/* Address */}
                {invoice?.header?.Address && (
                  <div className="rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 p-4 dark:from-indigo-950/30 dark:to-purple-950/30">
                    <div className="mb-2 flex items-center gap-2">
                      <Headset className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        العنوان
                      </span>
                    </div>
                    <p className="text-sm text-indigo-900 dark:text-indigo-100">
                      {invoice?.header?.Address}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Financial Summary Card */}
          <Card className="border-0 bg-gradient-to-br from-white/90 to-slate-50/90 shadow-xl backdrop-blur-sm dark:from-slate-900/90 dark:to-slate-800/90">
            <CardHeader className="border-b border-slate-200/50 bg-slate-50/30 px-6 py-4 dark:border-slate-700/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl text-slate-900 dark:text-slate-100">
                    الملخص المالي
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    تفاصيل الأرصدة والمدفوعات
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Balance Before */}
                <div className="rounded-xl bg-gradient-to-r from-slate-50 to-slate-100 p-4 dark:from-slate-800/50 dark:to-slate-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-slate-400"></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        الحساب قبل الفاتورة
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        {invoice?.header?.S_Balance !== undefined &&
                        invoice?.header?.S_Balance !== null
                          ? formatCurrency(invoice.header.S_Balance)
                          : 'غير متوفر'}
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-xs ${beforeBalance.color}`}
                      >
                        {beforeBalance.text}
                      </Badge>
                    </div>
                  </div>
                </div>
                {/* Invoice Total */}
                <div className="rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:from-blue-950/30 dark:to-indigo-950/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        إجمالي الفاتورة
                      </span>
                    </div>
                    <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {invoice?.header?.TotalInvoice !== undefined &&
                      invoice?.header?.TotalInvoice !== null
                        ? formatCurrency(invoice.header.TotalInvoice)
                        : 'غير متوفر'}
                    </div>
                  </div>
                </div>

                {/* Additional Discount */}
                {invoice?.header?.AddDiscount &&
                invoice?.header?.AddDiscount > 0 ? (
                  <div className="rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 p-4 dark:from-amber-950/30 dark:to-orange-950/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-amber-400"></div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          خصم إضافي
                        </span>
                      </div>
                      <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
                        {invoice?.header?.AddDiscount !== undefined &&
                        invoice?.header?.AddDiscount !== null
                          ? formatCurrency(invoice.header.AddDiscount)
                          : 'غير متوفر'}
                      </div>
                    </div>
                  </div>
                ) : null}
                {/* Balance After */}
                <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 p-4 dark:from-emerald-950/30 dark:to-teal-950/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        الحساب بعد الفاتورة
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {invoice?.header?.S_BalanceAfter !== undefined &&
                        invoice?.header?.S_BalanceAfter !== null
                          ? formatCurrency(invoice.header.S_BalanceAfter)
                          : 'غير متوفر'}
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-xs ${afterBalance.color}`}
                      >
                        {afterBalance.text}
                      </Badge>
                    </div>
                  </div>
                </div>
                {/* Tax Information */}
                {invoice?.header?.TaxPercent ? (
                  <div className="rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 p-4 dark:from-purple-950/30 dark:to-pink-950/30">
                    <div className="mb-3 flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        معلومات الضريبة
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center">
                        <p className="text-xs text-purple-600 dark:text-purple-400">
                          النسبة
                        </p>
                        <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                          {invoice?.header?.TaxPercent}%
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-purple-600 dark:text-purple-400">
                          القيمة
                        </p>
                        <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                          {invoice?.header?.Tax !== undefined &&
                          invoice?.header?.Tax !== null
                            ? formatCurrency(invoice.header.Tax)
                            : 'غير متوفر'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* Invoice Statistics Card */}
          <Card className="border-0 bg-gradient-to-br from-white/90 to-slate-50/90 shadow-xl backdrop-blur-sm dark:from-slate-900/90 dark:to-slate-800/90">
            <CardHeader className="border-b border-slate-200/50 bg-slate-50/30 px-6 py-4 dark:border-slate-700/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl text-slate-900 dark:text-slate-100">
                    إحصائيات الفاتورة
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    ملخص الأصناف والكميات
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:from-blue-950/30 dark:to-indigo-950/30">
                  <div className="mb-2 flex items-center gap-2">
                    <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      عدد الأصناف
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {invoice?.transactions?.length || 0}
                  </p>
                </div>

                <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 p-4 dark:from-emerald-950/30 dark:to-teal-950/30">
                  <div className="mb-2 flex items-center gap-2">
                    <Hash className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      إجمالي الكمية
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                    {invoice?.transactions?.reduce(
                      (sum, item) => sum + (item.Qty || 0),
                      0,
                    ) || 0}
                  </p>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    رقم الفاتورة
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {invoice?.header?.InvoiceID}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    حالة الفاتورة
                  </span>
                  <Badge
                    variant="outline"
                    className="border-green-200 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-300"
                  >
                    <AlertCircle className="mr-1 h-3 w-3" />
                    مكتملة
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Items Table */}
        <div className="space-y-6">
          <ItemsTable Items={invoice?.transactions} />
        </div>
      </div>
    </div>
  );
}

export default InvoiceBlock;
